'use client';

import { Children, type ReactNode, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Swiper as SwiperInstance } from 'swiper';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

interface SeoContentCarouselLabels {
  next: string;
  previous: string;
  slide: string;
}

interface SeoContentCarouselProps {
  children: ReactNode;
  labels: SeoContentCarouselLabels;
}

const CAROUSEL_CHEVRON_PATH = '/zeelool-next/goods-list/seo-footer/carousel-chevron.svg';
const AUTOPLAY_DELAY = 5000;

export default function SeoContentCarousel({ children, labels }: SeoContentCarouselProps) {
  const slides = Children.toArray(children);
  const hasMultipleSlides = slides.length > 1;
  const swiperRef = useRef<SwiperInstance | null>(null);
  const keyboardPausedRef = useRef(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!hasMultipleSlides || !swiper || typeof ResizeObserver === 'undefined') return;

    let wasHidden = false;
    const syncVisibility = () => {
      if (swiper.destroyed) return;
      const hidden = swiper.el.clientWidth === 0;
      if (hidden) {
        if (swiper.autoplay.running) swiper.autoplay.stop();
      } else if (wasHidden && !keyboardPausedRef.current) {
        // 先恢复尺寸和循环定位，避免零时长定位让刚启动的自动播放等待 transitionend。
        swiper.update();
        swiper.autoplay.start();
        if (swiper.el.matches(':hover') || document.visibilityState === 'hidden')
          swiper.autoplay.pause();
      }
      wasHidden = hidden;
    };

    syncVisibility();
    // Swiper 内置 resize 会忽略恢复到隐藏前相同尺寸的情况，需要观察实际显示状态。
    const observer = new ResizeObserver(syncVisibility);
    observer.observe(swiper.el);
    return () => observer.disconnect();
  }, [hasMultipleSlides]);

  if (slides.length === 0) return null;

  return (
    <div
      role="group"
      className="flex w-full flex-col gap-[19px] min-[920px]:gap-[24px]"
      onFocusCapture={(event) => {
        // 鼠标点击箭头留下的焦点不应阻止续播；键盘阅读时继续保留暂停行为。
        if (!hasMultipleSlides || !event.target.matches(':focus-visible')) return;
        keyboardPausedRef.current = true;
        swiperRef.current?.autoplay.stop();
      }}
      onBlurCapture={(event) => {
        if (
          !keyboardPausedRef.current ||
          (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget))
        )
          return;
        keyboardPausedRef.current = false;
        const swiper = swiperRef.current;
        if (!swiper || swiper.el.clientWidth === 0) return;
        swiper.autoplay.start();
        // 键盘离焦可能由点击正文触发，恢复时仍需尊重鼠标悬停。
        if (swiper.el.matches(':hover')) swiper.autoplay.pause();
      }}
    >
      <div
        data-seo-carousel-card
        className="relative overflow-hidden rounded-[8px] bg-white min-[920px]:min-h-[180px]"
      >
        <Swiper
          data-seo-carousel-viewport
          modules={[Autoplay]}
          slidesPerView={1}
          loop={hasMultipleSlides}
          loopPreventsSliding={false}
          allowTouchMove={hasMultipleSlides}
          grabCursor={hasMultipleSlides}
          speed={reducedMotion ? 0 : 300}
          autoplay={
            hasMultipleSlides
              ? {
                  delay: AUTOPLAY_DELAY,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }
              : false
          }
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
            if (hasMultipleSlides && swiper.el.clientWidth === 0) swiper.autoplay.stop();
          }}
          onRealIndexChange={(swiper) => setActiveIndex(swiper.realIndex)}
          onTransitionEnd={(swiper) => {
            // 零时长只触发 Swiper 事件，没有 DOM transitionend；保持悬停与键盘暂停。
            if (
              reducedMotion &&
              swiper.autoplay.paused &&
              !keyboardPausedRef.current &&
              !swiper.el.matches(':hover') &&
              document.visibilityState !== 'hidden'
            )
              swiper.autoplay.resume();
          }}
          className="!mx-0 w-full min-w-0 ![touch-action:pan-y_pinch-zoom] min-[920px]:min-h-[180px] [&>.swiper-wrapper]:h-auto [&>.swiper-wrapper]:min-h-[inherit] [&>.swiper-wrapper]:items-stretch"
        >
          {slides.map((slide, index) => (
            <SwiperSlide key={index} className="!flex !h-auto items-stretch">
              {slide}
            </SwiperSlide>
          ))}
          {hasMultipleSlides ? (
            <button
              slot="container-end"
              type="button"
              aria-label={labels.previous}
              className="absolute left-[28px] top-1/2 z-10 hidden size-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple min-[920px]:flex"
              onClick={() => {
                swiperRef.current?.slidePrev();
              }}
            >
              <Image
                src={CAROUSEL_CHEVRON_PATH}
                alt=""
                width={15}
                height={15}
                className="rotate-90"
              />
            </button>
          ) : null}
          {hasMultipleSlides ? (
            <button
              slot="container-end"
              type="button"
              aria-label={labels.next}
              className="absolute right-[28px] top-1/2 z-10 hidden size-[24px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/30 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple min-[920px]:flex"
              onClick={() => {
                swiperRef.current?.slideNext();
              }}
            >
              <Image
                src={CAROUSEL_CHEVRON_PATH}
                alt=""
                width={15}
                height={15}
                className="-rotate-90"
              />
            </button>
          ) : null}
        </Swiper>
      </div>

      {hasMultipleSlides ? (
        <div
          data-seo-carousel-pagination
          className="flex h-[3px] items-center justify-center gap-[8px]"
        >
          {slides.map((_, index) => (
            <button
              key={index}
              type="button"
              aria-current={activeIndex === index ? 'true' : undefined}
              aria-label={labels.slide.replace('{index}', String(index + 1))}
              className="flex h-[24px] cursor-pointer items-center justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-purple"
              onClick={() => {
                swiperRef.current?.slideToLoop(index);
              }}
            >
              <span
                className={`h-[3px] rounded-[99px] transition-[width,background-color] motion-reduce:transition-none ${
                  activeIndex === index
                    ? 'w-[40px] bg-primary-purple'
                    : 'w-[24px] bg-primary-purple-disable'
                }`}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
